import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchUsers, updateUser } from './adminApi';

const EMPTY_DRAFT = { email: '', is_subscribed: false, sub_tier: 'free', email_opt_in: true };

function AdminUsers() {
  const [queryText, setQueryText] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const noticeTimer = useRef(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchUsers({ q: queryText, page, per_page: 50 }, signal));
    } catch (loadError) {
      if (loadError.name !== 'AbortError') setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [queryText, page]);

  useEffect(() => {
    const controller = new AbortController();
    const handle = setTimeout(() => load(controller.signal), 250);
    return () => { clearTimeout(handle); controller.abort(); };
  }, [load]);

  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  const startEdit = (user) => {
    setEditingId(user.id);
    setDraft({
      email: user.email || '',
      is_subscribed: Boolean(user.is_subscribed),
      sub_tier: user.sub_tier || 'free',
      email_opt_in: Boolean(user.email_opt_in),
    });
    setError('');
  };

  const save = async (user) => {
    // Send only what actually changed, so an unrelated field can never be
    // rewritten by a stale draft.
    const changes = {};
    if (draft.email !== user.email) changes.email = draft.email;
    if (draft.is_subscribed !== Boolean(user.is_subscribed)) changes.is_subscribed = draft.is_subscribed;
    if (draft.sub_tier !== (user.sub_tier || 'free')) changes.sub_tier = draft.sub_tier;
    if (draft.email_opt_in !== Boolean(user.email_opt_in)) changes.email_opt_in = draft.email_opt_in;

    if (Object.keys(changes).length === 0) {
      setEditingId(null);
      return;
    }

    setSaving(true);
    try {
      await updateUser(user.id, changes);
      setNotice(`Updated ${Object.keys(changes).join(', ')} for ${user.email}.`);
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
      noticeTimer.current = setTimeout(() => setNotice(''), 6000);
      setEditingId(null);
      load();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const users = data?.data || [];
  const tiers = data?.allowed_sub_tiers || ['free', 'paid'];
  const totalPages = Math.max(Math.ceil((data?.total || 0) / (data?.per_page || 50)), 1);

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          className="admin-input admin-input--grow"
          type="search"
          value={queryText}
          onChange={(event) => { setQueryText(event.target.value); setPage(1); }}
          placeholder="Search by email"
          aria-label="Search users"
        />
      </div>

      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {error && <p className="admin-error" role="alert">{error}</p>}

      <p className="admin-meta-line">
        {loading ? 'Loading…' : `${data?.total || 0} accounts`}
      </p>

      <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Email</th>
              <th scope="col">Subscribed</th>
              <th scope="col">Tier</th>
              <th scope="col">Email opt-in</th>
              <th scope="col">Stripe</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading && (
              <tr><td colSpan={7} className="admin-empty">No accounts match that search.</td></tr>
            )}
            {users.map((user) => {
              const editing = editingId === user.id;
              return (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>
                    {editing ? (
                      <input
                        className="admin-input admin-input--block"
                        value={draft.email}
                        onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                        aria-label={`Email for account ${user.id}`}
                      />
                    ) : user.email}
                  </td>
                  <td>
                    {editing ? (
                      <input
                        type="checkbox"
                        checked={draft.is_subscribed}
                        onChange={(event) => setDraft({ ...draft, is_subscribed: event.target.checked })}
                        aria-label={`Subscribed for account ${user.id}`}
                      />
                    ) : (user.is_subscribed ? 'Yes' : 'No')}
                  </td>
                  <td>
                    {editing ? (
                      <select
                        className="admin-input"
                        value={draft.sub_tier}
                        onChange={(event) => setDraft({ ...draft, sub_tier: event.target.value })}
                        aria-label={`Tier for account ${user.id}`}
                      >
                        {tiers.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                      </select>
                    ) : (user.sub_tier || 'free')}
                  </td>
                  <td>
                    {editing ? (
                      <input
                        type="checkbox"
                        checked={draft.email_opt_in}
                        onChange={(event) => setDraft({ ...draft, email_opt_in: event.target.checked })}
                        aria-label={`Email opt-in for account ${user.id}`}
                      />
                    ) : (user.email_opt_in ? 'Yes' : 'No')}
                  </td>
                  <td>
                    {user.stripe_customer_id
                      ? <code title={user.stripe_customer_id}>linked</code>
                      : '—'}
                  </td>
                  <td className="admin-actions">
                    {editing ? (
                      <>
                        <button type="button" className="admin-button" disabled={saving} onClick={() => save(user)}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" className="admin-button" disabled={saving} onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button type="button" className="admin-button" onClick={() => startEdit(user)}>Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="admin-footnote">
        Passwords, Stripe customer IDs and reset tokens are intentionally not editable here —
        changing them breaks login, billing reconciliation or password resets in ways that only
        surface when a customer complains.
      </p>

      {totalPages > 1 && (
        <div className="admin-pager">
          <button type="button" className="admin-button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" className="admin-button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
